import React from 'react';
import { FormControl, MenuItem, Select } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  BLUEPRINT_STOREY_LEVEL_SELECT_MAX,
  formatBlueprintStoreyLevelLabel,
} from '@shared/helpers/design/blueprint-underlay.helpers';

type BlueprintStoreyLevelSelectProps = {
  value: number;
  /** Highest level offered — the project's storey count. */
  maxLevel?: number;
  disabled?: boolean;
  onChange: (level: number) => void;
  onOpen?: () => void;
  className?: string;
  /** Stretch to the parent width (step-2 storey box). */
  fullWidth?: boolean;
};

/**
 * Single Storey dropdown used in PDF import step 2 and the PDF panel — one
 * option per storey in the project. A page covers from this start level until
 * the next page’s start.
 */
export const BlueprintStoreyLevelSelect: React.FC<
  BlueprintStoreyLevelSelectProps
> = ({
  value,
  maxLevel = BLUEPRINT_STOREY_LEVEL_SELECT_MAX,
  disabled,
  onChange,
  onOpen,
  className,
  fullWidth = false,
}) => {
  const { classes, cx } = useBlueprintSelectStyles({ fullWidth });
  const max = Math.max(1, Math.floor(maxLevel) || 1);
  const clamped = Math.min(max, Math.max(1, Math.floor(value) || 1));
  const options = Array.from({ length: max }, (_, index) => index + 1);

  return (
    <FormControl
      size="small"
      disabled={disabled}
      className={cx(classes.control, className)}
    >
      <Select
        value={clamped}
        className={classes.select}
        renderValue={(selected) =>
          formatBlueprintStoreyLevelLabel(Number(selected))
        }
        onOpen={onOpen}
        onChange={(event) => onChange(Number(event.target.value))}
      >
        {options.map((level) => (
          <MenuItem
            key={level}
            value={level}
            sx={{
              fontSize: (theme) => theme.typography.body2.fontSize,
              fontWeight: (theme) => theme.typography.body2.fontWeight,
              lineHeight: (theme) => theme.typography.body2.lineHeight,
            }}
          >
            {formatBlueprintStoreyLevelLabel(level)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/** Borderless body2 select shared by the PDF import and panel dropdowns. */
export const useBlueprintSelectStyles = makeStyles<{ fullWidth: boolean }>()(
  (theme, { fullWidth }) => ({
    control: {
      width: fullWidth ? '100%' : 'auto',
      minWidth: 0,
      flex: fullWidth ? '1 1 auto' : '0 1 auto',
    },
    select: {
      width: fullWidth ? '100%' : 'auto',
      borderRadius: 6,
      transition: 'background-color 0.12s ease',
      backgroundColor: 'transparent',
      '&:hover': {
        backgroundColor: theme.palette.grey[200],
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent',
      },
      '& .MuiSelect-select': {
        display: 'flex',
        alignItems: 'center',
        minHeight: 0,
        boxSizing: 'border-box',
        paddingTop: theme.spacing(0.75),
        paddingBottom: theme.spacing(0.75),
        paddingLeft: theme.spacing(1.5),
        paddingRight: 28,
        // Match PDF panel body2 (Scale / Length / page labels).
        fontSize: theme.typography.body2.fontSize,
        fontWeight: theme.typography.body2.fontWeight,
        lineHeight: theme.typography.body2.lineHeight,
        whiteSpace: 'nowrap',
      },
      '& .MuiSelect-icon': {
        right: 4,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    },
  }),
);
